/**
 * Scaled-space feature (M3 plan R8, order `FEATURE_ORDER.scaled`, reach mode only): owns the scaled-space pass.
 *
 * - `init`: creates the pass, attaches it (the scaled target becomes the main scene's background, the baked sky the
 *   scaled scene's), adds its tuning group to the dev panel, registers the session enter hook `scaled`
 *   (`setSystem(scaledSpecFromLayout(layout))`) and builds the current system if the runtime already entered one.
 * - `frame`: resizes the target when the drawing buffer changed, then `update` with the float64 render origin and
 *   the camera's pose, then `render`, before `post.render()` samples the target. Allocation-free.
 * - `bus 'quality'` → `setPreset` (the target resolution).
 */
import { Vector2 } from 'three/webgpu'
import type { GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import type { SystemLayout } from '../../galaxy/layout/types'
import { createScaledSpace, scaledSpecFromLayout, type ScaledSpace } from '../../render/scaled'

export interface ScaledFeature extends GameFeature {
  /** The pass in reach mode once initialised (null in the sandbox); the debug API reads its stats. */
  readonly space: ScaledSpace | null
}

/** Frame body: resize on a drawing-buffer change, place, render. Allocation-free. */
export function frameScaled(space: ScaledSpace, ctx: GameContext, size: Vector2, last: Vector2): void {
  ctx.renderer.getDrawingBufferSize(size)
  if (size.x !== last.x || size.y !== last.y) {
    last.copy(size)
    space.setSize(size.x, size.y, 1)
  }
  space.update(ctx.rig.pos, ctx.camera.quaternion, ctx.camera.fov, ctx.camera.aspect, ctx.sim.time)
  space.render()
}

export function createScaledFeature(): ScaledFeature {
  let space: ScaledSpace | null = null
  let offQuality: (() => void) | null = null
  const size = new Vector2()
  const last = new Vector2(-1, -1)

  return {
    name: 'scaled',
    order: FEATURE_ORDER.scaled,
    get space(): ScaledSpace | null {
      return space
    },

    async init(ctx: GameContext): Promise<void> {
      const reach = ctx.reach
      if (reach === null) return
      const pass = createScaledSpace(ctx.renderer, ctx.quality)
      space = pass
      pass.attach(ctx.scene, ctx.env.skyBackground)
      reach.tuning.push(pass.tuning)
      reach.session.addEnterHook('scaled', (layout: SystemLayout) => pass.setSystem(scaledSpecFromLayout(layout)))
      offQuality = ctx.bus.on('quality', (preset) => pass.setPreset(preset))
      // The runtime enters its first system before the features initialise (plan §2.5 step 4).
      const current = reach.session.layout
      if (current !== null) await pass.setSystem(scaledSpecFromLayout(current))
    },

    frame(ctx: GameContext): void {
      if (space !== null) frameScaled(space, ctx, size, last)
    },

    dispose(): void {
      offQuality?.()
      offQuality = null
      space?.dispose()
      space = null
    },
  }
}

export default createScaledFeature()
